import { useState, useEffect } from "react";
import { Button } from "@twilio-paste/button";
import { Box } from "@twilio-paste/core";
import { ProductConversationsIcon } from "@twilio-paste/icons/esm/ProductConversationsIcon";

import { sendLoginCode, saveLogin, verifyLoginCode } from "../../api";
import ModalInputField from "../modals/ModalInputField";
import styles from "../../styles";
import TwilioLogo from "../icons/TwilioLogo";
import useAppAlert from "../../hooks/useAppAlerts";
import React from "react";

type SetTokenType = (token: string) => void;

interface LoginProps {
  setToken: SetTokenType;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

const Login: React.FC<LoginProps> = (props: LoginProps) => {
  const [login, setLogin] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [, AlertsView] = useAppAlert();

  const handleSendCode = async () => {
    if (!login.trim() || isWorking) {
      return;
    }
    setIsWorking(true);
    setFormError("");
    try {
      const sentTo = await sendLoginCode(login.trim());
      setCodeSent(true);
      setCode("");
      setNotice(
        sentTo ? `We texted a code to ${sentTo}.` : "We texted you a code."
      );
    } catch (error) {
      setFormError(errorMessage(error));
    }
    setIsWorking(false);
  };

  const handleVerifyCode = async () => {
    if (!code.trim() || isWorking) {
      return;
    }
    setIsWorking(true);
    setFormError("");
    try {
      const result = await verifyLoginCode(login.trim(), code.trim());
      if (!result.token) {
        setFormError("Received an empty token from backend.");
        setIsWorking(false);
        return;
      }
      saveLogin(result);
      props.setToken(result.token);
    } catch (error) {
      setFormError(errorMessage(error));
      setIsWorking(false);
    }
  };

  const handleSubmit = codeSent ? handleVerifyCode : handleSendCode;

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        handleSubmit();
      }
    };

    document.addEventListener("keydown", handleKeyPress);

    return function cleanup() {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [login, code, codeSent, isWorking]);

  // A link like ?user=6043301122 fills in the login
  useEffect(() => {
    const userParam = new URLSearchParams(window.location.search).get("user");
    if (userParam) {
      setLogin(userParam.trim());
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  return (
    <Box style={styles.loginContainer}>
      <AlertsView />
      <Box style={styles.loginContent}>
        <Box>
          <ProductConversationsIcon
            decorative={true}
            size="sizeIcon90"
            style={styles.logo}
            color="colorTextInverse"
          />
        </Box>
        <div style={styles.loginTitle}>Muchat</div>
        <div style={styles.subTitle}>Demo</div>
        <Box style={styles.loginForm}>
          <Box style={styles.userInput}>
            <ModalInputField
              label="Username"
              placeholder="e.g. 6043301122"
              isFocused={!codeSent}
              readonly={codeSent || isWorking}
              error={codeSent ? "" : formError}
              input={login}
              onChange={(value: string) => {
                setLogin(value);
                setFormError("");
              }}
              id="login-name"
            />
          </Box>
          {codeSent && (
            <Box style={styles.passwordInput}>
              <ModalInputField
                label="Code"
                placeholder="123456"
                isFocused={true}
                readonly={isWorking}
                maxLength={10}
                help_text={formError ? undefined : notice}
                error={formError}
                input={code}
                onChange={(value: string) => {
                  setCode(value);
                  setFormError("");
                }}
                id="code"
              />
            </Box>
          )}
          <Box style={styles.loginButton}>
            <Button
              fullWidth
              disabled={(codeSent ? !code.trim() : !login.trim()) || isWorking}
              variant="primary"
              onClick={handleSubmit}
              id="login"
              loading={isWorking}
            >
              {codeSent ? "Sign in" : "Text me a code"}
            </Button>
          </Box>
          {codeSent && (
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                margin: "-8px 24px 0",
                paddingBottom: "20px",
              }}
            >
              <Button
                variant="link"
                disabled={isWorking}
                onClick={() => {
                  setCodeSent(false);
                  setCode("");
                  setFormError("");
                }}
              >
                Change username
              </Button>
              <Button
                variant="link"
                disabled={isWorking}
                onClick={handleSendCode}
              >
                Resend code
              </Button>
            </Box>
          )}
        </Box>
        <Box style={{ paddingTop: 40 }}>
          <TwilioLogo />
        </Box>
      </Box>
      <Box style={styles.loginBackground}>
        <Box
          style={{
            height: "100%",
            width: "100%",
            backgroundColor: "#06033a",
            transform: "skewY(-12deg)",
            transformOrigin: "top right",
          }}
        />
      </Box>
    </Box>
  );
};

export default Login;
